import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2, Pencil, Sparkles, Trophy } from "lucide-react";
import { format, parseISO } from "date-fns";
import { useAuth } from "../lib/auth";

const MONTH_LABEL = (key) => {
  if (!key || key.length < 7) return key || "";
  const [y, m] = key.split("-");
  const d = new Date(parseInt(y), parseInt(m) - 1, 1);
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
};

export default function Successes() {
  const { user } = useAuth();
  const readOnly = user?.role === "viewer";
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const today = new Date();
  const currentMonthKey = today.toISOString().slice(0, 7);
  const [filterMonth, setFilterMonth] = useState(currentMonthKey);

  const load = async () => {
    const { data } = await api.get("/successes");
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  const monthsAvailable = useMemo(() => {
    const set = new Set([currentMonthKey]);
    items.forEach((s) => { if (s.date) set.add(s.date.slice(0, 7)); });
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [items, currentMonthKey]);

  const filtered = useMemo(() => items.filter((s) => (s.date || "").startsWith(filterMonth)), [items, filterMonth]);

  const remove = async (id) => {
    if (!window.confirm("Delete this success?")) return;
    try {
      await api.delete(`/successes/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="successes-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-amber-600">
            <Trophy size={12} /> Wins
          </div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900">This Month's Successes</h1>
          <p className="mt-2 text-sm text-zinc-600">Log the best things that have happened. Look back when you need a lift.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="successes-month-filter"
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
            className="rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-700 focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20"
          >
            {monthsAvailable.map((m) => (
              <option key={m} value={m}>
                {MONTH_LABEL(m)}{m === currentMonthKey ? " (current)" : ""}
              </option>
            ))}
          </select>
          {!readOnly && (
            <button
              data-testid="successes-add"
              onClick={() => setEditing({})}
              className="inline-flex items-center gap-1.5 rounded-md bg-[#0066ff] px-3 py-2 text-sm font-semibold text-white hover:bg-[#0052cc]"
            >
              <Plus size={14} /> Log a Win
            </button>
          )}
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50/60 to-white p-4 text-sm">
        <div className="flex items-center gap-2 text-amber-900">
          <Sparkles size={14} className="text-amber-500" />
          <span className="font-semibold">{filtered.length}</span>
          <span className="text-amber-800/80">win{filtered.length === 1 ? "" : "s"} in {MONTH_LABEL(filterMonth)}</span>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-3">
        {filtered.map((s) => (
          <SuccessCard
            key={s.id}
            item={s}
            readOnly={readOnly}
            onEdit={() => setEditing(s)}
            onDelete={() => remove(s.id)}
          />
        ))}
        {filtered.length === 0 && (
          <div className="rounded-xl border border-dashed border-zinc-200 bg-white p-10 text-center text-sm text-zinc-500">
            {filterMonth === currentMonthKey
              ? "Nothing logged yet this month. Add your first win!"
              : "No wins logged for this month."}
          </div>
        )}
      </div>

      {editing && (
        <SuccessModal
          initial={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SuccessCard({ item, readOnly, onEdit, onDelete }) {
  return (
    <div data-testid={`success-card-${item.id}`} className="group relative overflow-hidden rounded-xl border border-zinc-200 bg-white p-4 transition-all hover:-translate-y-[1px] hover:border-amber-300 hover:shadow-sm">
      <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-amber-400 to-amber-200" />
      <div className="flex items-start gap-3 pl-2">
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
          <Trophy size={14} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="whitespace-pre-wrap break-words text-sm text-zinc-900">{item.text}</div>
          <div className="mt-1 flex items-center gap-2 text-[11px] text-zinc-500">
            <span>{item.date ? format(parseISO(item.date), "MMM d, yyyy") : ""}</span>
            {item.category && (
              <span className="rounded-full bg-zinc-100 px-2 py-0.5 font-medium text-zinc-600">{item.category}</span>
            )}
          </div>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
            <button
              data-testid={`success-${item.id}-edit`}
              onClick={onEdit}
              className="rounded p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
              title="Edit"
            >
              <Pencil size={13} />
            </button>
            <button
              onClick={onDelete}
              className="rounded p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600"
              title="Delete"
            >
              <Trash2 size={13} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SuccessModal({ initial, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState(initial || { text: "", date: today, category: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.text?.trim()) { toast.error("What happened?"); return; }
    setSaving(true);
    try {
      const payload = {
        text: form.text.trim(),
        date: form.date || today,
        category: form.category?.trim() || null,
      };
      if (initial) await api.put(`/successes/${initial.id}`, payload);
      else await api.post("/successes", payload);
      toast.success(initial ? "Updated" : "Win logged!");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div data-testid="success-modal" className="w-full max-w-md rounded-2xl bg-white p-7 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h2 className="text-2xl font-semibold tracking-tight text-zinc-900">{initial ? "Edit Win" : "Log a Win"}</h2>
          <button onClick={onClose} className="rounded p-1 text-zinc-400 hover:bg-zinc-100"><X size={18} /></button>
        </div>

        <div className="mt-5 flex flex-col gap-4">
          <label className="text-sm">
            <span className="text-xs font-medium text-zinc-600">What happened?</span>
            <textarea
              data-testid="success-form-text"
              autoFocus
              rows={4}
              value={form.text}
              onChange={(e) => set("text", e.target.value)}
              placeholder="Closed Acme's $25k contract. Got a referral from Sara. Launched the new homepage…"
              className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="text-xs font-medium text-zinc-600">Date</span>
              <input
                data-testid="success-form-date"
                type="date"
                value={form.date || ""}
                onChange={(e) => set("date", e.target.value)}
                className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20"
              />
            </label>
            <label className="text-sm">
              <span className="text-xs font-medium text-zinc-600">Category <span className="text-zinc-400">(optional)</span></span>
              <input
                value={form.category || ""}
                onChange={(e) => set("category", e.target.value)}
                placeholder="Sales, Launch, Personal…"
                className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20"
              />
            </label>
          </div>
        </div>

        <div className="mt-7 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100">Cancel</button>
          <button data-testid="success-save" onClick={save} disabled={saving} className="rounded-md bg-[#0066ff] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0052cc] disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
